import { Fragment, useState } from 'react';
import clsx from 'clsx';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Card, InlineStat, StatTile, TrendPill } from '../../components/ui/Card';
import { Select } from '../../components/ui/Input';
import { Nil, TableFooter } from '../../components/ui/Table';
import { InfoTip } from '../../components/ui/InfoTip';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { formatDayMonth, formatDayMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { cleanText, daysAgo, humanize } from './format';
import { channelLabel, displayCompanyName, signalLabel } from './labels';
import { collapseJourney, journeyDetail } from './journey';
import { attioCompanyUrl } from '../Dashboard/csm/attio';
import { useAccountDossier, useHotAccounts, useSignalCoverage } from '../../hooks/useSignalOps';
import {
  BAND_LABELS,
  ICP_FIT_LABELS,
  type HotAccountRow,
  type ScoreBand,
  type IcpFit,
} from '../../api/signalOps';

/**
 * Momentum only when it moved: a warming or cooling account gets a pill, a
 * steady one gets nothing, so the column carries signal rather than noise.
 */
function TrendMark({ trend, delta, isNew }: { trend: string; delta: number; isNew?: boolean }) {
  if (isNew) {
    return (
      <span
        className="inline-flex rounded-sm bg-surface-tertiary px-1.5 py-0.5 text-body-xs font-medium text-text-tertiary"
        title="Not scored on the previous pass: its whole score is new, not a move"
      >
        new
      </span>
    );
  }
  // Steady rows stay blank: a dot on 13 of 15 rows said nothing (review #10).
  if (!hasMoved({ trend, delta })) return null;
  return (
    <TrendPill trend={trend === 'warming' ? 'up' : 'down'}>
      {delta >= 0 ? '+' : '−'}
      {Math.abs(delta).toFixed(1)}
    </TrendPill>
  );
}

/** A warming or cooling move big enough to show. */
function hasMoved(row: { trend: string; delta: number }): boolean {
  return (row.trend === 'warming' || row.trend === 'cooling') && Math.abs(row.delta) >= 0.1;
}

/**
 * A first score: the account was not on the previous pass, so the API's delta
 * is its whole score measured from 0 (nexxworks "+14.8"). Not a mover.
 */
function isNewScore(row: HotAccountRow): boolean {
  return row.delta > 0 && Math.abs(row.totalScore - row.delta) < 0.05;
}

/** Internal and test records that leaked into scoring. Hidden here; the data fix belongs upstream. */
function isInternalOrTest(row: HotAccountRow): boolean {
  return /^test\b/i.test(row.name) || /\bsequesto\b/i.test(row.name) || /(^|\.)sequesto\.(com|tech)$/i.test(row.domain ?? '');
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronRight
      className={clsx('h-4 w-4 shrink-0 text-text-muted transition-transform', open && 'rotate-90')}
      aria-hidden
    />
  );
}


/** Journey lines shown before "Show all": the panel grows with the page instead of scrolling inside itself. */
const JOURNEY_SHOWN = 12;

/** The faint dot every empty cell uses, as text for the stat components. */
const NIL = '·';

/** One labelled metric inside the expanded panel, with a plain-language hint. */
function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return <InlineStat label={label} value={value} sub={hint} size="sm" />;
}

/** The expanded panel: the full metric set explained, then the dated journey. */
function AccountDetail({ row }: { row: HotAccountRow }) {
  const { data, isLoading, isError, error } = useAccountDossier(row.companyId);
  const [showAllJourney, setShowAllJourney] = useState(false);
  // A signal type's home channel in the register (the one Signal model and Data health group by).
  // A touch can arrive through another channel (an article read from a newsletter link), and
  // then the line says "via" that channel instead of naming a second home for the signal.
  const coverage = useSignalCoverage();
  const homeChannel = new Map((coverage.data?.rows ?? []).map((r) => [r.signalType, r.channel]));

  const fitMultiplier = 0.4 + (row.fitScore / 100) * 0.6;
  const attioUrl = row.attioRecordId ? attioCompanyUrl(row.attioRecordId) : null;
  // The API's fresh-booking floor (registers.ts): an ICP-fit account that booked a
  // demo in the last 14 days scores at least the bottom of Warm, above intent × fit.
  const floored = row.totalScore > row.intentScore * fitMultiplier + 0.05;

  return (
    <div className="space-y-5 border-t border-border-light bg-surface-secondary/40 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* How the total is built, from scoring.ts: intent × (0.4 + 0.6 × fit/100). */}
        <p className="text-body-xs tabular-nums text-text-tertiary">
          Score {row.totalScore.toFixed(1)} = intent {row.intentScore.toFixed(1)} × fit multiplier{' '}
          {fitMultiplier.toFixed(2)} <span className="text-text-muted">(0.4 + 0.6 × fit {row.fitScore}/100)</span>
          {floored && (
            <span title="An ICP-fit account that booked a demo in the last 14 days scores at least the bottom of Warm (30)">
              {' '}· raised to {row.totalScore.toFixed(0)}: demo booked in the last 14 days
            </span>
          )}
        </p>
        {attioUrl && (
          <a
            href={attioUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-body-xs text-accent hover:underline"
          >
            Open in Attio
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
        <Metric label="Intent" value={row.intentScore.toFixed(1)} hint="Warmth from weighted signals, time-decayed" />
        <Metric label="Fit" value={String(row.fitScore)} hint="Firmographic match to our ICP" />
        <Metric
          label="ICP fit"
          value={row.icpFit ? ICP_FIT_LABELS[row.icpFit as IcpFit] ?? row.icpFit : NIL}
          hint="Core / adjacent / out of ICP"
        />
        {/* Final labels from the first paint: nothing here waits on the dossier, so the panel does not shift. */}
        <Metric label="Region" value={row.country ?? NIL} hint="Headquarters country" />
        <Metric label="Lead with" value={row.productToPitch ?? NIL} hint="Best product angle for the first touch" />
        <Metric label="Top channel" value={row.topChannel ? channelLabel(row.topChannel) : NIL} hint="Where most of its intent came from" />
        <Metric label="Signals" value={String(row.signalCount)} hint="Total ledger events on this account" />
        {/* Shown only when there is something on that side: an empty tile is noise. */}
        {row.retentionScore > 0 && (
          <Metric label="Retention" value={row.retentionScore.toFixed(1)} hint="Churn-risk side · kept apart, unscaled by fit" />
        )}
        {row.expansionScore > 0 && (
          <Metric label="Expansion" value={row.expansionScore.toFixed(1)} hint="Upsell side · kept apart, unscaled by fit" />
        )}
      </div>

      <div className="space-y-2">
        <p className="text-body-sm font-medium text-text-secondary">Journey, newest first</p>
        {isLoading ? (
          <LoadingState label="Loading the signal history…" />
        ) : isError ? (
          <ErrorState title="Couldn’t load the history" error={error} compact />
        ) : !data || data.journey.length === 0 ? (
          <p className="text-body-sm text-text-tertiary">No signals on the ledger for this account.</p>
        ) : (
          <ol className="space-y-1.5">
            {collapseJourney(data.journey)
              .slice(0, showAllJourney ? undefined : JOURNEY_SHOWN)
              .map(({ event: j, count }, i) => (
              <li key={`${j.occurredAt}-${i}`} className="flex flex-wrap items-baseline gap-2 text-body-sm">
                <span className="w-20 shrink-0 tabular-nums text-text-tertiary" title={formatDayMonthYear(j.occurredAt)}>
                  {formatDayMonth(j.occurredAt)}
                </span>
                <span className="text-text-primary">
                  {signalLabel(j.type)}
                  {count > 1 && <span className="ml-1 tabular-nums text-text-tertiary">×{count}</span>}
                </span>
                <span className="text-body-xs text-text-tertiary">
                  {homeChannel.has(j.type) && homeChannel.get(j.type) !== j.channel
                    ? `via ${channelLabel(j.channel)}`
                    : channelLabel(j.channel)}
                  {!j.isProspectAction && ' · we caused this'}
                </span>
                {journeyDetail(j.payload) && (
                  <span className="text-body-xs text-text-muted">{journeyDetail(j.payload)}</span>
                )}
              </li>
            ))}
            {!showAllJourney && collapseJourney(data.journey).length > JOURNEY_SHOWN && (
              <li>
                <button
                  type="button"
                  onClick={() => setShowAllJourney(true)}
                  className="text-body-xs text-accent hover:underline"
                >
                  Show all {collapseJourney(data.journey).length} lines
                </button>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  );
}

/** A collapsed row — the few metrics that decide who to call, then expand for the rest. */
function AccountRow({
  row,
  rank,
  open,
  onToggle,
}: {
  row: HotAccountRow;
  rank: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={clsx(
          'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors sm:gap-4',
          open ? 'bg-surface-secondary/60' : 'hover:bg-surface-secondary',
        )}
      >
        <span className="w-5 shrink-0 text-right text-body-xs tabular-nums text-text-tertiary">{rank}</span>

        {/* Company — the identity, shares the slack with the last signal */}
        <span className="flex min-w-0 flex-[2] flex-col">
          <span className="break-words text-body-sm font-medium leading-snug text-text-primary" title={cleanText(row.name)}>
            {displayCompanyName(cleanText(row.name))}
          </span>
          {row.domain && (
            <span className="truncate text-body-xs text-text-tertiary" title={row.domain}>
              {row.domain}
            </span>
          )}
        </span>

        {/* Score — the headline; its band is the group above. A mover or first score carries its mark beside it,
            so most rows (steady ones) show no empty Trend column. */}
        <span className="flex w-32 shrink-0 items-center justify-end gap-2 text-body-md tabular-nums text-text-primary">
          <span className="text-body-sm">
            <TrendMark trend={row.trend} delta={row.delta} isNew={isNewScore(row)} />
          </span>
          <span className="w-12 text-right">{row.totalScore.toFixed(1)}</span>
        </span>

        {/* Qualifier */}
        <span className="hidden w-24 shrink-0 md:block">
          {row.icpFit ? (
            <Badge>{ICP_FIT_LABELS[row.icpFit as IcpFit] ?? row.icpFit}</Badge>
          ) : (
            <Nil />
          )}
        </span>

        {/* Why now */}
        <span className="hidden min-w-0 flex-[3] flex-col lg:flex">
          <span className="text-body-sm text-text-secondary">{daysAgo(row.lastSignalAt) || <Nil />}</span>
          <span className="truncate text-body-xs text-text-tertiary" title={signalLabel(row.lastSignalType)}>
            {signalLabel(row.lastSignalType)}
          </span>
        </span>

        <Chevron open={open} />
      </button>

      {open && <AccountDetail row={row} />}
    </div>
  );
}

const HOW_IT_WORKS = (
  <div className="space-y-2">
    <p>
      Rows show what decides who to call: score, momentum, ICP fit and the last signal. Open a row for how the score
      is built, the retention and expansion sides, the dated journey and the Attio record.
    </p>
    <p>
      Score = intent × (0.4 + 0.6 × fit ÷ 100). Intent is the time-decayed sum of signal weights (half-life 21
      days); fit is the 0–100 firmographic match, so a perfect fit keeps all of the intent and a zero fit keeps 40%.
      Scores are uncapped on purpose, because normalising to 100 would flatten the gap between the top row and the
      rest. Retention and expansion are kept apart and unscaled by fit.
    </p>
  </div>
);

const BAND_ORDER: ScoreBand[] = ['hot', 'warm', 'cool', 'cold'];

function bandRank(band: string): number {
  const i = BAND_ORDER.indexOf(band as ScoreBand);
  return i === -1 ? BAND_ORDER.length : i;
}

/** The band's only colour: a small dot in its group header. Hot reads strongest, warm amber, the rest recede. */
const BAND_DOT: Record<ScoreBand, string> = {
  hot: 'bg-error',
  warm: 'bg-warning',
  cool: 'bg-text-tertiary',
  cold: 'bg-border-dark',
};

function BandHeader({ band, count, avg }: { band: string; count: number; avg: number }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 bg-surface-secondary px-4 py-2">
      <span className="flex items-center gap-2 text-body-sm font-semibold text-text-primary">
        <span
          aria-hidden
          className={clsx('h-2 w-2 rounded-full', BAND_DOT[band as ScoreBand] ?? 'bg-border-dark')}
        />
        {BAND_LABELS[band as ScoreBand] ?? humanize(band)}
      </span>
      <span className="text-body-xs text-text-tertiary">
        {count} {count === 1 ? 'account' : 'accounts'} · avg score {avg.toFixed(0)}
      </span>
    </div>
  );
}

export function AccountsView() {
  const [band, setBand] = useState<string>('');
  const [icpFit, setIcpFit] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  // The API's maximum; the footer says "Top N of M" if even that is not everything.
  const params: { limit: number; band?: string; icpFit?: string } = { limit: 500 };
  if (band) params.band = band;
  if (icpFit) params.icpFit = icpFit;

  const { data, isLoading, isError, error, refetch } = useHotAccounts(params);
  // Warmest band first, then score within the band, so each band is one contiguous group.
  const allRows = data?.rows ?? [];
  const rows = allRows
    .filter((r) => !isInternalOrTest(r))
    .sort((a, z) => bandRank(a.band) - bandRank(z.band) || z.totalScore - a.totalScore);
  const hiddenInternal = allRows.length - rows.length;
  const groups = new Map<string, { count: number; sum: number }>();
  for (const r of rows) {
    const g = groups.get(r.band) ?? { count: 0, sum: 0 };
    g.count += 1;
    g.sum += r.totalScore;
    groups.set(r.band, g);
  }
  const pager = usePagination(rows, 15);
  const total = data?.total ?? allRows.length;
  const capped = total > allRows.length;
  const warm = rows.filter((r) => r.band === 'hot' || r.band === 'warm').length;
  const scored = data?.scoreDate ? `scored ${formatDayMonthYear(data.scoreDate)}` : 'not scored yet';
  // New accounts score from 0, so their whole score reads as a "move": counted apart.
  const newlyScored = rows.filter(isNewScore).length;
  const movers = [...rows]
    .filter((r) => r.trend === 'warming' && r.delta > 0 && !isNewScore(r))
    .sort((a, z) => z.delta - a.delta);
  const topMover = movers[0];
  const filtered = !!band || !!icpFit;

  return (
    <div className="space-y-4">
      {!isLoading && !isError && data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile
            label="Warm accounts"
            value={`${formatNumber(warm)}${capped && warm === rows.length ? '+' : ''}`}
            sub={`Hot or warm · ${scored}${filtered ? ' · filtered' : ''}`}
          />
          <StatTile
            label="New this week"
            value={data.newWarmThisWeek === undefined ? 'Unavailable' : formatNumber(data.newWarmThisWeek)}
            sub="Turned hot or warm in the last 7 days · all accounts"
          />
          <StatTile
            label="Biggest mover"
            value={topMover ? `+${topMover.delta.toFixed(1)}` : 'None'}
            sub={
              // "Movers" are score rises between the last two passes; "New this week" counts
              // accounts entering hot or warm over 7 days. Different questions, so different words.
              (topMover
                ? `${displayCompanyName(cleanText(topMover.name))}: largest score rise since the previous scoring pass${
                    movers.length > 1 ? ` · ${formatNumber(movers.length)} scores rose` : ''
                  }`
                : 'No score rose since the previous scoring pass') +
              (newlyScored > 0 ? ` · ${newlyScored} newly scored, not counted` : '')
            }
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <p className="text-body-sm text-text-tertiary">
          {data?.scoreDate ? `Warmest first · scored ${formatDayMonthYear(data.scoreDate)}` : isLoading ? '' : 'No scores yet'}
        </p>
        <InfoTip>{HOW_IT_WORKS}</InfoTip>
        <div className="ml-auto flex flex-wrap gap-2">
          <Select size="sm" id="gtm-band-filter" aria-label="Band" value={band} onChange={(e) => setBand(e.target.value)}>
            <option value="">All bands</option>
            {(['hot', 'warm', 'cool', 'cold'] as ScoreBand[]).map((b) => (
              <option key={b} value={b}>
                {BAND_LABELS[b]}
              </option>
            ))}
          </Select>
          <Select size="sm" id="gtm-icp-filter" aria-label="ICP fit" value={icpFit} onChange={(e) => setIcpFit(e.target.value)}>
            <option value="">All ICP fits</option>
            {(['core', 'adjacent', 'out'] as IcpFit[]).map((f) => (
              <option key={f} value={f}>
                {ICP_FIT_LABELS[f]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState title="Couldn’t load accounts" error={error} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing scored yet"
          description="Accounts appear once the signal pipeline has resolved records, filled the ledger and scored them."
        />
      ) : (
        <div>
          <Card padding="none" className="divide-y divide-border-light overflow-hidden">
            <div className="hidden items-center gap-3 px-4 py-2.5 font-label text-eyebrow font-bold uppercase text-text-tertiary sm:flex sm:gap-4">
              <span className="w-5 text-right">#</span>
              <span className="min-w-0 flex-[2]">Company</span>
              <span className="w-32 text-right">Score</span>
              <span className="hidden w-24 md:block">ICP fit</span>
              <span className="hidden min-w-0 flex-[3] lg:block">Last signal</span>
              <span className="w-4" />
            </div>
            {pager.pageItems.map((row, i) => {
              const startsGroup = i === 0 || pager.pageItems[i - 1]!.band !== row.band;
              const group = groups.get(row.band);
              return (
                <Fragment key={row.companyId}>
                  {startsGroup && group && (
                    <BandHeader band={row.band} count={group.count} avg={group.sum / group.count} />
                  )}
                  <AccountRow
                    row={row}
                    rank={(pager.page - 1) * pager.pageSize + i + 1}
                    open={expanded === row.companyId}
                    onToggle={() => setExpanded((cur) => (cur === row.companyId ? null : row.companyId))}
                  />
                </Fragment>
              );
            })}
          </Card>
          <TableFooter>
            {capped
              ? `Top ${formatNumber(allRows.length)} of ${formatNumber(total)} scored accounts`
              : `${formatNumber(rows.length)} ${rows.length === 1 ? 'account' : 'accounts'}`}
            {BAND_ORDER.filter((b) => groups.has(b))
              .map((b) => ` · ${groups.get(b)!.count} ${BAND_LABELS[b].toLowerCase()}`)
              .join('')}
            {hiddenInternal > 0 && ` · ${hiddenInternal} internal or test ${hiddenInternal === 1 ? 'record' : 'records'} hidden`}
          </TableFooter>
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
          />
        </div>
      )}
    </div>
  );
}
